import { FC, useCallback, useMemo } from "react";

// Shared lib imports
import { clsxMerge } from "shared/lib/helpers";
import { ActionMenu } from "shared/ui/action-menu";
import { modalHelpers } from "shared/lib/helpers/modalHelpers";
import { ContactDetailI } from "shared/lib/interfaces/account";

// Internal app imports
// Components
import {
  ACCOUNT_SIDEBAR_ACTION_BUTTON_CLASSNAME,
  ACCOUNT_STATUSES_PROTECTED_FROM_DELETE,
} from "@/modules/pipeline/account-details/constants";

// Context
import { useAccountDetailsContext } from "@/modules/pipeline/account-details/context";
import { getLeadStateFlags } from "@/modules/pipeline/account-details/utils";

// Modal IDs
import { DISQUALIFY_MODAL_ID } from "@/modals/disqualify-modal";
import { EDIT_CONTACT_MODAL_ID } from "@/modals/edit-contact-modal";
import { DNC_NUMBERS_TOGGLE_MODAL_ID } from "@/components/modals/dnc-numbers-toggle-modal";
import { SET_CONTACT_TYPE_MODAL_ID } from "@/modals/set-contact-type-modal";
import { DELETE_ACCOUNT_LEAD_CONFIRMATION_MODAL_ID } from "@/modals/delete-account-lead-confirmation-modal";
import { SET_UNSET_PRIMARY_LEAD_CONFIRMATION_MODAL_ID } from "@/modals/set-primary-lead-confirmation-modal";

// Icons
import {
  TagIcon,
  UserIcon,
  UserXIcon,
  CircleOffIcon,
  PhoneMissedIcon,
  PersonStandingIcon,
} from "lucide-react";
import { useInCallContext } from "@/hooks/dialer/use-dialer-context";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";

interface ProspectEditMenuPropsI {
  contact?: ContactDetailI;
}

const getSetLabel = (currentValue: boolean) => (currentValue ? "Unset" : "Set");

const MenuItem = ({
  icon: Icon,
  title,
  handler,
}: {
  icon: any;
  title: string;
  handler: () => void;
}) => (
  <span
    onClick={handler}
    className={clsxMerge(
      "flex w-[182px] items-center gap-2",
      "cursor-pointer",
      "rounded-md p-2 hover:bg-black/5",
      "typography-body-4"
    )}
  >
    <Icon className="h-4 w-4 min-w-4" /> <span>{title}</span>
  </span>
);

export const ProspectEditMenu: FC<
  ProspectEditMenuPropsI & PropsWithClassNameI
> = ({ contact, className }) => {
  const { accountStatus, contacts } = useAccountDetailsContext();
  const { contact: callingContact } = useInCallContext();

  // make sure contact doesn't have any significant upcoming events like a booked meeting
  const checkIfContactDeletionDisabled = useCallback(
    (contact: ContactDetailI) =>
      accountStatus?.status &&
      accountStatus?.primary_contact_id === contact.id &&
      ACCOUNT_STATUSES_PROTECTED_FROM_DELETE.find(
        (status) => status === accountStatus?.status
      ),
    [accountStatus]
  );

  const { isLeadDisqualified: isDisqualified, isLeadMarkedDNC: isMarkedDNC } =
    useMemo(
      () => getLeadStateFlags(contact as ContactDetailI),
      [contact?.contact_disposition_type]
    );

  const isCurrentlyCalling = callingContact?.id === contact?.id;
  const isDeleteContactAllowed =
    (contacts?.length || 0) > 1 &&
    !checkIfContactDeletionDisabled(contact as ContactDetailI);

  const { setFocusedContact } = useAccountDetailsContext();

  const isPrimaryContact = !!contact?.is_primary_lead;

  const actions = useMemo(() => {
    const basicOptions = [
      {
        content: (
          <MenuItem
            icon={UserIcon}
            title="Edit lead"
            handler={() => {
              setFocusedContact(contact);
              modalHelpers.lazyOpen(EDIT_CONTACT_MODAL_ID);
            }}
          />
        ),
      },
      {
        content: (
          <MenuItem
            icon={PersonStandingIcon}
            title={`${getSetLabel(isPrimaryContact)} as primary lead`}
            handler={() => {
              setFocusedContact(contact);
              modalHelpers.lazyOpen(
                SET_UNSET_PRIMARY_LEAD_CONFIRMATION_MODAL_ID
              );
            }}
          />
        ),
      },
      {
        content: (
          <MenuItem
            icon={TagIcon}
            title="Set contact type"
            handler={() => {
              setFocusedContact(contact);
              modalHelpers.lazyOpen(SET_CONTACT_TYPE_MODAL_ID);
            }}
          />
        ),
      },
    ];

    if (!isMarkedDNC) {
      basicOptions.push({
        content: (
          <MenuItem
            icon={PhoneMissedIcon}
            title="Mark Phone DNCs"
            handler={() => {
              setFocusedContact(contact);
              modalHelpers.lazyOpen(DNC_NUMBERS_TOGGLE_MODAL_ID);
            }}
          />
        ),
      });
    }

    if (!isDisqualified && !isCurrentlyCalling && !isMarkedDNC) {
      basicOptions.push({
        content: (
          <MenuItem
            icon={CircleOffIcon}
            title="Disqualify"
            handler={() => {
              setFocusedContact(contact);
              modalHelpers.lazyOpen(DISQUALIFY_MODAL_ID);
            }}
          />
        ),
      });
    }

    if (isDeleteContactAllowed && !isCurrentlyCalling) {
      basicOptions.push({
        content: (
          <MenuItem
            icon={UserXIcon}
            title="Delete"
            handler={() => {
              setFocusedContact(contact);
              modalHelpers.lazyOpen(DELETE_ACCOUNT_LEAD_CONFIRMATION_MODAL_ID);
            }}
          />
        ),
      });
    }

    return basicOptions;
  }, [
    contact,
    isDeleteContactAllowed,
    isPrimaryContact,
    isDisqualified,
    isCurrentlyCalling,
    isMarkedDNC,
    setFocusedContact,
  ]);

  return (
    <ActionMenu
      className={className}
      actions={actions}
      trigger={
        <button
          className={clsxMerge(
            ACCOUNT_SIDEBAR_ACTION_BUTTON_CLASSNAME,
            "-translate-y-1"
          )}
        >
          Edit
        </button>
      }
    />
  );
};
